/**
 * Touch 'n Go eWallet receipt-text parser.
 *
 * TNG receipts differ from Maybank's in three ways that matter here:
 *   - the amount often carries an explicit sign ("- RM 12.90" spent,
 *     "+ RM 50.00" reloaded), which is a strong type signal;
 *   - reloads and cashbacks are common and must not be counted as spending;
 *   - the merchant line frequently keeps an acquirer prefix, which the
 *     merchant normaliser strips later.
 *
 * Same discipline as every parser here: no field is invented, and every field
 * carries a confidence score.
 */
import { DEFAULT_TIME_ZONE } from '../time.js';
import type { TransactionType } from '../schemas.js';
import {
  combineConfidence,
  datePartsToIso,
  findAmounts,
  normalizeReceiptText,
  parseReceiptDate,
  valueAfterLabel,
  valueBelowLabel,
  type Extracted,
  type ParseOptions,
  type ParseResult,
} from './common.js';

const SIGNATURES = [
  "touch 'n go",
  'touch n go',
  'touch  n go',
  'tng ewallet',
  'tng e-wallet',
  'tngd',
  'tng digital',
  'ewallet balance',
  'go+',
];

const AMOUNT_LABELS = ['amount', 'total', 'payment amount', 'transaction amount', 'paid'];
const MERCHANT_LABELS = [
  'merchant',
  'merchant name',
  'paid to',
  'pay to',
  'to',
  'recipient',
  'receiver',
  'store',
  'outlet',
  // Cashback and incoming transfers name the sender.
  'from',
];
const DATE_LABELS = [
  'transaction date',
  'date & time',
  'date and time',
  'date/time',
  'datetime',
  'date',
  'paid on',
  'time',
];
const REFERENCE_LABELS = [
  'transaction id',
  'transaction no',
  'reference id',
  'reference no',
  'ref no',
  'ref id',
  'order id',
  'receipt no',
  'wallet transaction id',
];

const MERCHANT_BLOCKLIST = [
  "touch 'n go",
  'touch n go',
  'tng ewallet',
  'tng digital',
  'ewallet',
  'go+',
  'successful',
  'payment successful',
  'transaction successful',
  'reload successful',
];

export function looksLikeTngReceipt(raw: string): boolean {
  const text = raw.toLowerCase();
  return SIGNATURES.some((signature) => text.includes(signature));
}

function detectType(lines: readonly string[]): Extracted<TransactionType> | undefined {
  const haystack = lines.join(' \n ').toLowerCase();

  const checks: { pattern: RegExp; type: TransactionType; confidence: number }[] = [
    { pattern: /\b(refund|reversal|reversed|money back)\b/, type: 'refund', confidence: 0.9 },
    { pattern: /\b(cashback|cash back|rebate)\b/, type: 'income', confidence: 0.85 },
    { pattern: /\b(reload|top[- ]?up|topup|tambah nilai|add money)\b/, type: 'transfer', confidence: 0.92 },
    { pattern: /\b(transfer to|send money|duitnow transfer|wallet transfer)\b/, type: 'transfer', confidence: 0.9 },
    { pattern: /\b(received|money received|incoming)\b/, type: 'income', confidence: 0.8 },
    { pattern: /\b(payment|paid|purchase|toll|parking|spend)\b/, type: 'expense', confidence: 0.85 },
  ];

  for (const check of checks) {
    if (check.pattern.test(haystack)) {
      const evidence = lines.find((line) => check.pattern.test(line.toLowerCase())) ?? '';
      return { value: check.type, confidence: check.confidence, evidence };
    }
  }
  return undefined;
}

/** A leading sign on the amount tells us the direction outright. */
function signedTypeHint(line: string): TransactionType | undefined {
  if (/^\s*[-−]\s*(?:RM|MYR)/i.test(line)) return 'expense';
  if (/^\s*\+\s*(?:RM|MYR)/i.test(line)) return 'income';
  return undefined;
}

function extractAmount(
  lines: readonly string[],
): { amount: Extracted<number>; signHint?: TransactionType } | undefined {
  for (const [index, line] of lines.entries()) {
    const labelled = valueAfterLabel(line, AMOUNT_LABELS);
    if (labelled) {
      const [amount] = findAmounts(labelled);
      if (amount !== undefined && amount > 0) {
        const hint = signedTypeHint(labelled);
        return {
          amount: { value: amount, confidence: 0.97, evidence: line },
          ...(hint ? { signHint: hint } : {}),
        };
      }
    }
    if (AMOUNT_LABELS.includes(line.toLowerCase().replace(/[:\s]+$/, ''))) {
      const below = valueBelowLabel(lines, index);
      const [amount] = below ? findAmounts(below) : [];
      if (amount !== undefined && amount > 0) {
        const hint = below ? signedTypeHint(below) : undefined;
        return {
          amount: { value: amount, confidence: 0.95, evidence: `${line} / ${below ?? ''}`.trim() },
          ...(hint ? { signHint: hint } : {}),
        };
      }
    }
  }

  // A bare signed amount line is the classic TNG receipt headline.
  const signed = lines.find((line) => signedTypeHint(line) !== undefined);
  if (signed) {
    const [amount] = findAmounts(signed);
    if (amount !== undefined && amount > 0) {
      const hint = signedTypeHint(signed);
      return {
        amount: { value: amount, confidence: 0.93, evidence: signed },
        ...(hint ? { signHint: hint } : {}),
      };
    }
  }

  const candidates = lines
    .filter((line) => !/\b(balance|baki|available|remaining|limit)\b/i.test(line))
    .flatMap((line) =>
      findAmounts(line)
        .filter((amount) => amount > 0)
        .map((amount) => ({ amount, line })),
    )
    .filter((entry) => /RM|MYR/i.test(entry.line));

  const first = candidates[0];
  if (!first) return undefined;
  return {
    amount: {
      value: first.amount,
      confidence: candidates.length === 1 ? 0.87 : 0.68,
      evidence: first.line,
    },
  };
}

function isBlockedMerchant(value: string): boolean {
  const lower = value.toLowerCase().trim();
  return MERCHANT_BLOCKLIST.some((blocked) => lower === blocked || lower.startsWith(`${blocked} `));
}

function extractMerchant(lines: readonly string[]): Extracted<string> | undefined {
  for (const [index, line] of lines.entries()) {
    const labelled = valueAfterLabel(line, MERCHANT_LABELS);
    if (labelled && !isBlockedMerchant(labelled) && !/^[-+]?\s*RM\b/i.test(labelled)) {
      const cleaned = labelled.replace(/\s{2,}/g, ' ').trim();
      if (cleaned.length >= 2) return { value: cleaned, confidence: 0.92, evidence: line };
    }
    const bare = line.toLowerCase().replace(/[:\s]+$/, '');
    if (MERCHANT_LABELS.includes(bare)) {
      const below = valueBelowLabel(lines, index);
      if (below && !isBlockedMerchant(below) && !/^[-+]?\s*RM\b/i.test(below)) {
        return { value: below, confidence: 0.88, evidence: `${line} / ${below}` };
      }
    }
  }
  return undefined;
}

function extractDate(
  lines: readonly string[],
  options: ParseOptions,
): Extracted<string> | undefined {
  const timeZone = options.timeZone ?? DEFAULT_TIME_ZONE;

  for (const line of lines) {
    const labelled = valueAfterLabel(line, DATE_LABELS);
    if (!labelled) continue;
    const parsed = parseReceiptDate(labelled, options);
    if (parsed) {
      return {
        value: datePartsToIso(parsed.parts, timeZone),
        confidence: Math.min(0.97, parsed.confidence + 0.02),
        evidence: line,
      };
    }
  }

  for (const line of lines) {
    const parsed = parseReceiptDate(line, options);
    if (parsed) {
      return {
        value: datePartsToIso(parsed.parts, timeZone),
        confidence: parsed.confidence * 0.95,
        evidence: line,
      };
    }
  }
  return undefined;
}

function extractReference(lines: readonly string[]): Extracted<string> | undefined {
  for (const line of lines) {
    const labelled = valueAfterLabel(line, REFERENCE_LABELS);
    if (labelled && /[A-Za-z0-9]{6,}/.test(labelled)) {
      return { value: labelled.slice(0, 64), confidence: 0.95, evidence: line };
    }
  }
  return undefined;
}

/** Parse Touch 'n Go eWallet receipt text. */
export function parseTngReceipt(raw: string, options: ParseOptions = {}): ParseResult {
  const lines = normalizeReceiptText(raw);
  if (lines.length === 0) {
    return { ok: false, parser: 'tng', reason: 'The receipt text is empty.' };
  }

  const found = extractAmount(lines);
  if (!found) {
    return {
      ok: false,
      parser: 'tng',
      reason: 'No Ringgit amount found in this receipt. Nothing was imported.',
    };
  }

  const merchant = extractMerchant(lines);
  const occurredAt = extractDate(lines, options);
  const reference = extractReference(lines);

  const keywordType = detectType(lines);
  // A keyword ("Reload") beats a sign, because a reload is also shown as "- RM".
  const type: Extracted<TransactionType> | undefined =
    keywordType ??
    (found.signHint
      ? { value: found.signHint, confidence: 0.75, evidence: found.amount.evidence }
      : undefined);

  const warnings: string[] = [];
  if (!merchant) warnings.push('No merchant found.');
  if (!occurredAt) warnings.push('No transaction date found; the import time will be used.');
  if (!type) warnings.push('Could not tell whether this is a payment, reload or refund.');
  if (!looksLikeTngReceipt(raw)) {
    warnings.push('This does not clearly look like a Touch ’n Go eWallet receipt.');
  }

  const scores = [
    found.amount.confidence,
    merchant?.confidence ?? 0.35,
    occurredAt?.confidence ?? 0.3,
    type?.confidence ?? 0.5,
  ];

  return {
    ok: true,
    parser: 'tng',
    amountSen: found.amount,
    ...(merchant ? { merchant } : {}),
    ...(occurredAt ? { occurredAt } : {}),
    ...(reference ? { reference } : {}),
    ...(type ? { type } : {}),
    currency: 'MYR',
    confidence: combineConfidence(scores),
    warnings,
  };
}
